import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useLayoutEffect, useState } from 'react';
import { surveyTitleByType } from './SurveyCard';
import { Box, Button, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Survey } from './models/Survey';
import {
  useAnalytics,
  METRICS_SURVEY_PSS_ACTIVITY_ACCESS,
  METRICS_SURVEY_CLIENT_PACKET_ACTIVITY_ACCESS,
  METRICS_SURVEY_CSAT_ACTIVITY_ACCESS,
  METRICS_SURVEY_PSS_ACTIVITY_COMPLETE,
  METRICS_SURVEY_CLIENT_PACKET_ACTIVITY_COMPLETE,
  METRICS_SURVEY_CSAT_ACTIVITY_COMPLETE,
} from '@xolv-mybrightlink/common';

type SurveyType = Survey['type'];
type AnalyticsActionType = 'ACCESS' | 'COMPLETE';

type AdobeSignEvent = {
  type: 'ESIGN' | 'REJECT' | 'PREFILL';
  data: Record<string, unknown>;
};

function getAnalyticsActionByType(
  type: SurveyType,
  action: AnalyticsActionType,
) {
  const isAccess = action == 'ACCESS';
  switch (type) {
    case 'PSS':
      return isAccess
        ? METRICS_SURVEY_PSS_ACTIVITY_ACCESS
        : METRICS_SURVEY_PSS_ACTIVITY_COMPLETE;
    case 'CLIENT_PACKET':
      return isAccess
        ? METRICS_SURVEY_CLIENT_PACKET_ACTIVITY_ACCESS
        : METRICS_SURVEY_CLIENT_PACKET_ACTIVITY_COMPLETE;
    case 'CSAT':
      return isAccess
        ? METRICS_SURVEY_CSAT_ACTIVITY_ACCESS
        : METRICS_SURVEY_CSAT_ACTIVITY_COMPLETE;
  }
}

export function SurveyDetail() {
  const location = useLocation();
  const surveyLink = location.state?.surveyLink;
  const type = location.state?.type as SurveyType;
  const navigate = useNavigate();
  const [popupVisible, setPopupVisible] = useState(false);
  const analytics = useAnalytics();

  useLayoutEffect(() => {
    analytics.trackAction(getAnalyticsActionByType(type, 'ACCESS'));

    function handleIframeEvent(message: MessageEvent) {
      const origin = message.origin?.toString();
      if (!origin) return;

      // handle for adobe sign
      if (origin.includes('adobesign') || origin.includes('echosign')) {
        try {
          const data = JSON.parse(message.data) as AdobeSignEvent;
          if (data?.type === 'ESIGN') {
            analytics.trackAction(
              getAnalyticsActionByType('CLIENT_PACKET', 'COMPLETE'),
            );
            setPopupVisible(true);
          }
        } catch (e) {
          console.warn('Failed to parse adobe sign event', e);
        }
      }
      // handle for qualtrics
      if (origin.includes('qualtrics')) {
        if (message.data?.includes('closeQSIWindow')) {
          analytics.trackAction(getAnalyticsActionByType(type, 'COMPLETE'));
          setPopupVisible(true);
        }
      }
    }

    window.addEventListener('message', handleIframeEvent);
    const elem: HTMLElement | null = document.querySelector('.hide-scrollbar');
    if (elem) {
      elem.style.display = 'none';
    }

    return () => {
      window.removeEventListener('message', handleIframeEvent);
      if (elem) {
        elem.style.display = 'block';
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleClosePopup() {
    if (type == 'PSS' || type == 'CLIENT_PACKET') {
      return setPopupVisible(false);
    }
    navigate('/mybrightlink');
  }

  function renderPopup() {
    return (
      popupVisible && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            opacity: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <div
            style={{
              minWidth: '20rem',
              display: 'flex',
              alignItems: 'center',
              flexFlow: 'column',
              background: '#FFFFFF',
              borderRadius: '.5em',
              boxShadow: '2px 3px 5px #999',
              opacity: 1,
              padding: '1em 3em',
            }}
          >
            <Typography variant={'h6'} sx={{ fontWeight: 500, mt: 5 }}>
              <FormattedMessage id='survey.dialog.title' />
            </Typography>
            <Typography variant={'h6'} sx={{ mt: 5 }}>
              <FormattedMessage id='survey.dialog.content' />
            </Typography>
            <Button
              onClick={handleClosePopup}
              variant='text'
              sx={{ mt: 5, width: 150, borderRadius: 8 }}
              style={{ background: '#307FE2', color: '#ffffff' }}
            >
              <FormattedMessage id='survey.dialog.confirm' />
            </Button>
          </div>
        </div>
      )
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: '#ffffff',
        overflow: 'hidden',
      }}
    >
      {renderPopup()}
      <Box sx={{ backgroundColor: '#ffffff', height: '3em' }}>
        <Typography variant='subtitle1' fontWeight='500' sx={{ mx: 3, my: 3 }}>
          <FormattedMessage id={surveyTitleByType[type]} />
        </Typography>
      </Box>
      {surveyLink && (
        <div style={{ height: 'calc(100% - 3em)' }}>
          <iframe
            style={{ border: 'none' }}
            width={'100%'}
            height={'100%'}
            src={surveyLink}
          ></iframe>
        </div>
      )}
    </div>
  );
}
